import { z } from 'zod/v4';

/**
 * Statuses a questionnaire can be created with — mirrors
 * `QUESTIONNAIRE_STATUS_ENUM` in the legacy
 * `routes/schemas/v1/constants.js`, restricted to the three values the legacy
 * `create.body` schema accepted.
 */
export const QuestionnaireStatuses = ['ACTIVE', 'COMPLETED', 'DRAFT'] as const;

export type QuestionnaireStatus = (typeof QuestionnaireStatuses)[number];

/**
 * Body of `POST /api/questionnaires`, ported from the legacy `create.body`
 * JSON Schema.
 *
 * Unknown keys are stripped rather than rejected (the zod object default).
 * That matches the legacy behaviour: the 2025-06 schema declared
 * `additionalProperties: false`, but Fastify's AJV config removed extras
 * silently instead of answering 400 — which is why the frontend's
 * `fromExistingAssessment` flag has always been accepted and then ignored.
 */
export const CreateQuestionnaireBodySchema = z.object({
  name: z.string().min(1).max(255),
  status: z.enum(QuestionnaireStatuses),
  description: z.string().min(1).max(1024).optional(),
  dueDate: z.iso.datetime().optional(),
  /**
   * Clone source. Any questionnaire the caller's company owns, or an active
   * Retraced template, may be used — see `getClonableTemplateDb`.
   */
  templateId: z.union([z.string().min(1), z.null()]).optional(),
});

export type CreateQuestionnaireBody = z.infer<
  typeof CreateQuestionnaireBodySchema
>;

/**
 * The created questionnaire as the read-back SELECT returns it.
 *
 * `looseObject` for the same reason as `QuestionnaireListRowSchema`: the audit
 * columns ride along on every row and date columns are left undeclared so they
 * pass through as `Date` and serialise to ISO strings.
 *
 * Two columns the legacy `getById` projection omitted — `templateId` and
 * `isActive` — are selected here. Both are already written by the INSERT, and
 * the legacy response silently dropped them.
 */
export const CreatedQuestionnaireSchema = z.looseObject({
  id: z.string(),
  companyId: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  isRetraced: z.string().nullable().optional(),
  // Integer flags, not booleans — surfaced as the 0/1 the table stores.
  isTemplate: z.number().nullable().optional(),
  isArchived: z.number().nullable().optional(),
  isActive: z.number().nullable().optional(),
  templateId: z.string().nullable().optional(),
  statistics: z.looseObject({}).nullable().optional(),
});

export type CreatedQuestionnaire = z.infer<typeof CreatedQuestionnaireSchema>;
